import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ndfApi } from '../../services/ndf-api';
import { BoutonPrincipal, BoutonSecondaire, Champ, dateFr, Etiquette, Saisie, Selection } from './ui/Champ';
import { erreurGlobale, erreursParChamp, type ErreursChamps } from './ui/erreurs-champs';
import { useToast } from '../../context/ToastContext';

/**
 * Délégation de ses approbations pendant une absence (R-VAL-04).
 *
 * Placé sur l'écran « À valider » : c'est là qu'on pense à ses approbations, et
 * la veille d'un départ en congés, c'est la dernière chose qu'on ferait si elle
 * était enfouie dans un écran d'administration.
 *
 * On délègue SES approbations, pas son poste : pendant la période, le
 * remplaçant est le seul prévenu, mais les deux peuvent approuver — un retour
 * anticipé ne doit pas obliger à défaire la délégation.
 */
export function BlocDelegation() {
  const toast = useToast();
  const qc = useQueryClient();
  const [ouvert, setOuvert] = useState(false);
  const [erreurs, setErreurs] = useState<ErreursChamps>({});
  const [f, setF] = useState({ delegue_id: '', debut: '', fin: '', motif: '' });

  const delegations = useQuery({
    queryKey: ['ndf', 'delegations'],
    queryFn: () => ndfApi.notes.delegations(),
    retry: false,
  });
  const utilisateurs = useQuery({
    queryKey: ['ndf', 'utilisateurs'],
    queryFn: () => ndfApi.utilisateurs(),
    enabled: ouvert,
    retry: false,
  });

  const creer = useMutation({
    mutationFn: () =>
      ndfApi.notes.creerDelegation({
        delegue_id: f.delegue_id,
        debut: f.debut,
        fin: f.fin,
        motif: f.motif.trim() || null,
      }),
    onSuccess: () => {
      toast.success('Délégation enregistrée.');
      setOuvert(false);
      setF({ delegue_id: '', debut: '', fin: '', motif: '' });
      setErreurs({});
      qc.invalidateQueries({ queryKey: ['ndf', 'delegations'] });
    },
    onError: (err) => {
      setErreurs(erreursParChamp(err));
      toast.error(erreurGlobale(err) ?? 'La délégation n’a pas pu être enregistrée.');
    },
  });

  const supprimer = useMutation({
    mutationFn: (id: string) => ndfApi.notes.supprimerDelegation(id),
    onSuccess: () => {
      toast.success('Délégation retirée.');
      qc.invalidateQueries({ queryKey: ['ndf', 'delegations'] });
    },
    onError: (err) => toast.error(erreurGlobale(err) ?? 'Le retrait a échoué.'),
  });

  const liste = delegations.data?.delegations ?? [];
  const aujourdhui = new Date().toLocaleDateString('sv-SE');

  return (
    <section className="rounded-lg border border-nuit/10 bg-white p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="font-titre text-sm font-bold text-nuit">Délégation d’approbation</h2>
        <BoutonSecondaire type="button" onClick={() => setOuvert((v) => !v)}>
          {ouvert ? 'Annuler' : 'Déléguer pendant une absence'}
        </BoutonSecondaire>
      </div>

      {liste.length > 0 && (
        <ul className="mt-2 space-y-1 text-sm">
          {liste.map((d) => {
            const enCours = d.debut <= aujourdhui && aujourdhui <= d.fin;
            return (
              <li key={d.id} className="flex flex-wrap items-center justify-between gap-2">
                <span className="text-nuit">
                  {d.utilisateur_email} → <strong>{d.delegue_email}</strong>, du {dateFr(d.debut)} au{' '}
                  {dateFr(d.fin)}
                  {d.motif ? ` (${d.motif})` : ''}
                </span>
                <span className="flex shrink-0 items-center gap-2">
                  {enCours ? <Etiquette ton="actif">En cours</Etiquette> : <Etiquette>À venir</Etiquette>}
                  <button
                    type="button"
                    onClick={() => supprimer.mutate(d.id)}
                    className="min-h-11 text-sm text-nuit underline"
                  >
                    Retirer
                  </button>
                </span>
              </li>
            );
          })}
        </ul>
      )}

      {ouvert && (
        <div className="mt-3 space-y-3 border-t border-nuit/10 pt-3">
          <p className="text-sm text-nuit/70">
            Les premier et dernier jours d’absences sont inclus dans cette délégation. Votre
            remplaçant désigné sera le seul notifié pour des notes de frais à approuver. Vous
            pouvez à tout moment annuler cette délégation en cliquant sur « Retirer ».
          </p>

          <div className="grid gap-3 sm:grid-cols-2">
            <Champ label="Qui vous remplace" requis erreur={erreurs.delegue_id}>
              <Selection
                value={f.delegue_id}
                onChange={(e) => setF({ ...f, delegue_id: e.target.value })}
              >
                <option value="">Choisir…</option>
                {(utilisateurs.data?.utilisateurs ?? []).map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.nom ?? u.email}
                  </option>
                ))}
              </Selection>
            </Champ>
            <Champ label="Motif" aide="Congés, formation… Facultatif.">
              <Saisie value={f.motif} onChange={(e) => setF({ ...f, motif: e.target.value })} />
            </Champ>
            {/* La mention est portée par les DEUX dates. Ne la mettre que sur
                la fin laisse croire que le début, lui, ne serait pas compris -
                et la question revient à chaque saisie. */}
            <Champ label="Premier jour d’absence" requis erreur={erreurs.debut} aide="Ce jour est inclus.">
              <Saisie
                type="date"
                value={f.debut}
                onChange={(e) => setF({ ...f, debut: e.target.value })}
              />
            </Champ>
            <Champ label="Dernier jour d’absence" requis erreur={erreurs.fin} aide="Ce jour est inclus.">
              <Saisie type="date" value={f.fin} onChange={(e) => setF({ ...f, fin: e.target.value })} />
            </Champ>
          </div>

          <BoutonPrincipal
            type="button"
            disabled={!f.delegue_id || !f.debut || !f.fin || creer.isPending}
            onClick={() => creer.mutate()}
          >
            {creer.isPending ? 'Enregistrement…' : 'Enregistrer la délégation'}
          </BoutonPrincipal>
        </div>
      )}
    </section>
  );
}
